import { ChevronRight } from 'lucide-react';
import { ReactNode, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert.tsx';
import { Badge } from '@/components/ui/badge.tsx';
import { Button } from '@/components/ui/button.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx';
import { Textarea } from '@/components/ui/textarea.tsx';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemTitle,
} from '@/components/ui/item.tsx';
import { cn } from 'cn';
import { pageContentClassName } from '../../ui/pageContentClass';

export function SettingsSubPageShell({
  title,
  backTo,
  children,
}: {
  title: string;
  backTo: string;
  children: ReactNode;
}) {
  const navigate = useNavigate();

  return (
    <div className={pageContentClassName}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
        <Button type="button" variant="ghost" size="sm" onClick={() => navigate(backTo)}>
          Назад
        </Button>
      </div>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  );
}

export function SettingsHubPage({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className={pageContentClassName}>
      <h1 className="mb-4 text-xl font-semibold tracking-tight">{title}</h1>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  );
}

export function SettingsGroupCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card size="sm" className="gap-0 py-0 shadow-sm">
      <CardHeader className="border-b border-border/60 py-3">
        <CardTitle className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-0 py-0">{children}</CardContent>
    </Card>
  );
}

export function SettingsNavItem({
  title,
  subTitle,
  to,
  isLast = false,
  disabled = false,
}: {
  title: string;
  subTitle?: string;
  to?: string;
  isLast?: boolean;
  disabled?: boolean;
}) {
  const navigate = useNavigate();

  return (
    <Item
      size="sm"
      variant="default"
      className={cn(
        'rounded-none border-0 border-b border-border/60 px-4',
        disabled
          ? 'cursor-not-allowed opacity-55'
          : 'cursor-pointer hover:bg-muted/50',
        isLast && 'border-b-0',
      )}
      onClick={disabled || !to ? undefined : () => navigate(to)}
    >
      <ItemContent>
        <ItemTitle>{title}</ItemTitle>
        {subTitle ? <ItemDescription>{subTitle}</ItemDescription> : null}
      </ItemContent>
      <ItemActions>
        <ChevronRight className="size-4 text-muted-foreground" aria-hidden />
      </ItemActions>
    </Item>
  );
}

export function SettingsFormCard({
  title,
  description,
  children,
}: {
  title?: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Card size="sm" className="shadow-sm">
      {(title || description) && (
        <CardHeader className="pb-2">
          {title ? <CardTitle>{title}</CardTitle> : null}
          {description ? <CardDescription>{description}</CardDescription> : null}
        </CardHeader>
      )}
      <CardContent>{children}</CardContent>
    </Card>
  );
}

export function SettingsForm({
  onSubmit,
  children,
  className,
}: {
  onSubmit: () => void | Promise<void>;
  children: ReactNode;
  className?: string;
}) {
  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    void onSubmit();
  }

  return (
    <form onSubmit={handleSubmit} className={cn('flex flex-col gap-3', className)}>
      {children}
    </form>
  );
}

export function SettingsErrorAlert({ children }: { children: ReactNode }) {
  return (
    <Alert variant="destructive">
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  );
}

export function SettingsSuccessAlert({ children }: { children: ReactNode }) {
  return (
    <Alert>
      <AlertDescription className="text-foreground">{children}</AlertDescription>
    </Alert>
  );
}

export function SettingsHint({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn('text-sm text-muted-foreground', className)}>{children}</p>;
}

export function SettingsStatusLine({
  label,
  ok,
  error,
}: {
  label: string;
  ok?: boolean;
  error?: string;
}) {
  const text = integrationStatusLabel(ok, error);
  return (
    <p className="text-sm">
      {label}:{' '}
      <Badge variant={ok ? 'default' : 'destructive'} className="align-middle">
        {text}
      </Badge>
    </p>
  );
}

export function Field({
  label,
  id,
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  label: string;
  id: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

export function TextAreaField({
  label,
  id,
  value,
  onChange,
}: {
  label: string;
  id: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Textarea id={id} value={value} className="min-h-28" onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

export function NumberField({
  label,
  id,
  value,
  onChange,
}: {
  label: string;
  id: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        value={String(value)}
        onChange={(event) => onChange(Number(event.target.value) || 0)}
      />
    </div>
  );
}

export function SelectField<T extends string>({
  label,
  id,
  value,
  onChange,
  options,
  placeholder,
  className,
}: {
  label: string;
  id?: string;
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
  placeholder?: string;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={id}>{label}</Label>
      <Select
        value={value}
        items={options.map((option) => ({ value: option.value, label: option.label }))}
        onValueChange={(next) => {
          if (next) onChange(next as T);
        }}
      >
        <SelectTrigger id={id} className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function splitList(value: string): string[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

export function integrationStatusLabel(ok: boolean | undefined, error?: string): string {
  if (ok) return 'Подключён';
  if (error) return error;
  return 'Не настроен';
}

export { ItemGroup };
